import { MarketCar } from "@/types/market";

export function filterBySearchTermHandler(makeNameMap: Map<string, string>) {
  const normalize = (str: string) =>
    str
      .normalize("NFD")
      .replace(/\p{Diacritic}/gu, "")
      .toLowerCase();

  return (cars: MarketCar[], term: string) => {
    const normTerm = normalize(term || "");
    const tokens = normTerm.split(/\s+/).filter(Boolean);

    if (tokens.length === 0) return cars;

    return cars.filter((car) => {
      const make = makeNameMap.get(car.make_id) || car.make_name || "";
      const model = car.model_name || "";
      const combined = normalize(`${make} ${model}`);

      // Ensure every token from the search term exists somewhere in the combined string
      return tokens.every((tok) => combined.includes(tok));
    });
  };
}

export const filterByYearHandler = (cars: MarketCar[], yearRanges: string[]): MarketCar[] => {
  if (!yearRanges || yearRanges.length === 0) return cars;

  return cars.filter((car) => {
    const years = car.production_years?.split("-").map(Number);
    const buildYear = years?.[0];

    if (!buildYear || isNaN(buildYear)) return false;

    return yearRanges.some((range) => {
      const [start, end] = range.split("-").map(Number);
      return buildYear >= start && buildYear <= end;
    });
  });
};

export const filterByMakeHandler = (cars: MarketCar[], makeId: string | null) => {
  if (!makeId) return cars;
  return cars.filter((car) => car.make_id === makeId);
};

export const filterByDecade = (cars: MarketCar[], selectedDecades: string[]) => {
  if (!selectedDecades.length) return cars;

  const getProductionStartYear = (productionYears: string) => {
    const matchProduction = productionYears.match(/^(\d{4})/);
    if (!matchProduction) return undefined;
    const year = parseInt(matchProduction[1], 10);
    return Number.isNaN(year) ? undefined : year;
  };

  return cars.filter((car) => {
    const carYear = getProductionStartYear(car.production_years);
    if (carYear === undefined) return false;

    const matched = selectedDecades.some((decadeRaw) => {
      const decade = (decadeRaw || "").trim();

      if (decade === "pre-war") {
        const result = carYear < 1950;
        return result;
      }

      // Accept formats like "1950s" or short-form "50s"
      // Try full-year decade first (e.g. 1950s)
      const fullMatch = decade.match(/^(\d{4})s$/);
      if (fullMatch) {
        const start = parseInt(fullMatch[1], 10);
        const end = start + 9;
        const result = carYear >= start && carYear <= end;
        return result;
      }

      // Try short form like "50s" -> assume 1900s (1950s)
      const shortMatch = decade.match(/^(\d{2})s$/);
      if (shortMatch) {
        const two = parseInt(shortMatch[1], 10);
        const start = 1900 + two;
        const end = start + 9;
        const result = carYear >= start && carYear <= end;
        return result;
      }

      return false;
    });

    return matched;
  });
};
